class KolTrustCarousel extends HTMLElement {
  connectedCallback() {
    this._viewport = this.querySelector('.kol-trust-carousel__viewport');
    this._track = this.querySelector('.kol-trust-carousel__track');
    this._sourceGroup = this.querySelector('.kol-trust-carousel__group');
    this._resumeAnimation = this._resumeAnimation.bind(this);
    this._pauseAnimation = this._pauseAnimation.bind(this);
    this._syncClones();
    this._syncDuration();
    this._bindMobileDrag();
    this._setupReveal();
    this._resizeObserver = new ResizeObserver(() => {
      this._syncClones();
      this._syncDuration();
    });
    this._resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    this._unbindMobileDrag();
    this._revealObserver?.disconnect();
  }

  _syncClones() {
    if (!this._track || !this._sourceGroup) {
      return;
    }

    this._track.querySelectorAll('.kol-trust-carousel__group[data-carousel-clone="true"]').forEach((clone) => clone.remove());

    const groupWidth = this._sourceGroup.scrollWidth;
    const viewportWidth = this.querySelector('.kol-trust-carousel__viewport')?.clientWidth || window.innerWidth;

    if (groupWidth === 0) {
      return;
    }

    const cloneCount = Math.max(2, Math.ceil((viewportWidth * 2) / groupWidth) + 1);

    for (let index = 0; index < cloneCount; index += 1) {
      const clone = this._sourceGroup.cloneNode(true);
      clone.dataset.carouselClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      this._track.appendChild(clone);
    }
  }

  _syncDuration() {
    const track = this._track || this.querySelector('.kol-trust-carousel__track');
    const group = this._sourceGroup || this.querySelector('.kol-trust-carousel__group');

    if (!track || !group) {
      return;
    }

    const baseDuration = Number(this.dataset.duration || 42);
    const slideCount = Number(this.dataset.slideCount || 1);
    const measuredWidth = group.scrollWidth;

    if (slideCount <= 1 || measuredWidth === 0) {
      track.style.animation = 'none';
      return;
    }

    const normalizedDuration = Math.max(baseDuration, Math.round(measuredWidth / 14));
    track.style.removeProperty('animation');
    track.style.setProperty('--kol-trust-group-width', `${measuredWidth + this._getGap()}px`);
    track.style.setProperty('--kol-trust-duration', `${normalizedDuration}s`);
  }

  _bindMobileDrag() {
    if (!this._viewport || !this._track) {
      return;
    }

    this._viewport.addEventListener('touchstart', this._pauseAnimation, { passive: true });
    this._viewport.addEventListener('touchend', this._resumeAnimation, { passive: true });
    this._viewport.addEventListener('touchcancel', this._resumeAnimation, { passive: true });
    this._viewport.addEventListener('pointerdown', this._pauseAnimation);
    this._viewport.addEventListener('pointerup', this._resumeAnimation);
    this._viewport.addEventListener('pointercancel', this._resumeAnimation);
    this._viewport.addEventListener('mouseleave', this._resumeAnimation);
  }

  _unbindMobileDrag() {
    if (!this._viewport) {
      return;
    }

    this._viewport.removeEventListener('touchstart', this._pauseAnimation);
    this._viewport.removeEventListener('touchend', this._resumeAnimation);
    this._viewport.removeEventListener('touchcancel', this._resumeAnimation);
    this._viewport.removeEventListener('pointerdown', this._pauseAnimation);
    this._viewport.removeEventListener('pointerup', this._resumeAnimation);
    this._viewport.removeEventListener('pointercancel', this._resumeAnimation);
    this._viewport.removeEventListener('mouseleave', this._resumeAnimation);
  }

  _pauseAnimation() {
    if (window.matchMedia('(max-width: 740px)').matches && this._track) {
      this._track.style.animationPlayState = 'paused';
    }
  }

  _resumeAnimation() {
    if (this._track) {
      this._track.style.animationPlayState = '';
    }
  }

  _getGap() {
    if (!this._track) {
      return 0;
    }

    return parseFloat(getComputedStyle(this._track).columnGap || getComputedStyle(this._track).gap || 0) || 0;
  }

  _setupReveal() {
    if (this.dataset.revealBound === 'true') {
      return;
    }

    this.dataset.revealBound = 'true';
    this.classList.add('is-reveal-ready');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('is-visible');
            this._revealObserver?.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.16 }
    );
    this._revealObserver.observe(this);
  }
}

if (!customElements.get('kol-trust-carousel')) {
  customElements.define('kol-trust-carousel', KolTrustCarousel);
}
